import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Alert,
	Button,
	Box,
	Stack,
	Typography,
} from '@mui/material'
import { CheckCircle, Cancel, PauseCircle } from '@mui/icons-material'
import React, { useState, useEffect } from 'react'
import { ScaffoldRequestForm } from '../../../types/Scaffold/ScaffoldRequest'
import { updateScaffoldRequestDecision } from '../../../services/scaffoldRequestService'
import { getDecisionIcon, getDecisionSeverity } from './DecisionUtils'

interface DecisionDialogProps {
	open: boolean
	scaffoldRequest: ScaffoldRequestForm
	onClose: () => void
	refreshScaffolds: () => Promise<void>
}

export default function DecisionDialog({
	open,
	scaffoldRequest,
	onClose,
	refreshScaffolds,
}: DecisionDialogProps) {
	const [selectedDecision, setSelectedDecision] = useState<'Approved' | 'Declined' | 'OnHold' | ''>('')
	const [decisionComment, setDecisionComment] = useState('')
	const [isUpdatingDecision, setIsUpdatingDecision] = useState(false)

	// Initialize form when dialog opens or scaffoldRequest changes
	useEffect(() => {
		if (open) {
			setSelectedDecision(scaffoldRequest.approvalDecision || '')
			setDecisionComment(scaffoldRequest.decisionComment || '')
		}
	}, [open, scaffoldRequest.approvalDecision, scaffoldRequest.decisionComment])

	const handleClose = () => {
		setSelectedDecision('')
		setDecisionComment('')
		onClose()
	}

	const handleUpdateDecision = async () => {
		if (!selectedDecision || !scaffoldRequest.id) return

		setIsUpdatingDecision(true)
		try {
			await updateScaffoldRequestDecision(
				scaffoldRequest.id,
				selectedDecision as 'Approved' | 'Declined' | 'OnHold',
				decisionComment,
			)
			await refreshScaffolds()
			handleClose()
		} catch (error) {
			console.error('Error updating decision:', error)
		} finally {
			setIsUpdatingDecision(false)
		}
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='sm'
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Typography variant='h6' sx={{ fontWeight: 600 }}>
					{scaffoldRequest.approvalDecision ? 'Update Decision' : 'Set Decision'}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ mt: 1 }}>
					<FormControl fullWidth required>
						<InputLabel id='decision-select-label'>Decision</InputLabel>
						<Select
							labelId='decision-select-label'
							id='decision-select'
							value={selectedDecision}
							label='Decision'
							onChange={(e) =>
								setSelectedDecision(e.target.value as 'Approved' | 'Declined' | 'OnHold')
							}
						>
							<MenuItem value='Approved'>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<CheckCircle fontSize='small' color='success' />
									<Typography>Approved</Typography>
								</Box>
							</MenuItem>
							<MenuItem value='Declined'>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Cancel fontSize='small' color='error' />
									<Typography>Declined</Typography>
								</Box>
							</MenuItem>
							<MenuItem value='OnHold'>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<PauseCircle fontSize='small' color='warning' />
									<Typography>On Hold</Typography>
								</Box>
							</MenuItem>
						</Select>
					</FormControl>

					<TextField
						fullWidth
						multiline
						rows={4}
						label='Comment'
						placeholder='Add a comment about this decision...'
						value={decisionComment}
						onChange={(e) => setDecisionComment(e.target.value)}
						variant='outlined'
					/>

					{selectedDecision && (
						<Alert
							severity={getDecisionSeverity(selectedDecision)}
							icon={getDecisionIcon(selectedDecision) || undefined}
						>
							<Typography variant='body2'>
								You are about to set the decision to <strong>{selectedDecision}</strong>
								{decisionComment && ' with a comment.'}
							</Typography>
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={isUpdatingDecision}>
					Cancel
				</Button>
				<Button
					onClick={handleUpdateDecision}
					variant='contained'
					disabled={!selectedDecision || isUpdatingDecision}
					startIcon={
						selectedDecision === 'Approved' ? (
							<CheckCircle />
						) : selectedDecision === 'Declined' ? (
							<Cancel />
						) : (
							<PauseCircle />
						)
					}
				>
					{isUpdatingDecision ? 'Updating...' : 'Update Decision'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

